import { PromoBoxProps } from "./types";
import { cn } from "../../utils/cn";
import ResponsiveImage from "../ResponsiveImage";

// Define reusable grid position styles
// NOTE: Ensure all classes are in tailwind safelist
export const gridPositions = {
  Col3Row3Span3Rows: "tw-col-[1_/_span_1] tw-row-[4_/_span_2] md:tw-col-[2_/_span_1] md:tw-row-[2_/_span_2] xl:tw-col-[3_/_span_1] xl:tw-row-[3_/_span_3]",
  Col1Row8Span2Rows: "tw-col-[1_/_span_1] tw-row-[8_/_span_1] md:tw-row-[8_/_span_2]",
  Col3Row13Span1Row: "tw-col-[1_/_span_1] tw-row-[13_/_span_1] md:tw-col-[2_/_span_1] xl:tw-col-[3_/_span_1]",
  Col1Row3Span3Rows: "tw-col-[1_/_span_1] tw-row-[3_/_span_3] md:tw-col-[1_/_span_1] md:tw-row-[3_/_span_2] xl:tw-col-[1_/_span_1] xl:tw-row-[3_/_span_3]",
  Col1Row10Span3Cols: "tw-col-[1_/_span_1] tw-row-[10_/_span_1] md:tw-col-[1_/_span_2] md:tw-row-[10_/_span_1] xl:tw-col-[1_/_span_3] xl:tw-row-[10_/_span_1]",
  Col2Row3Span2RowsSpan2Cols: "tw-col-[1_/_span_1] tw-row-[3_/_span_2] md:tw-col-[1_/_span_2] md:tw-row-[3_/_span_2] xl:tw-col-[2_/_span_2] xl:tw-row-[3_/_span_2]",
  Col1Row9Span1RowsSpan1Cols: "tw-col-[1_/_span_1] tw-row-[9_/_span_1] md:tw-col-[1_/_span_1] md:tw-row-[9_/_span_1] xl:tw-col-[1_/_span_1] xl:tw-row-[9_/_span_1]",
  Col1Row4Span2RowsSpan2Cols: "tw-col-[1_/_span_1] tw-row-[4_/_span_2] md:tw-col-[1_/_span_2] md:tw-row-[4_/_span_2] xl:tw-col-[1_/_span_2] xl:tw-row-[4_/_span_2]",
  Col2Row13Span1RowSpan2Cols: "tw-col-[1_/_span_1] tw-row-[13_/_span_1] md:tw-col-[1_/_span_2] md:tw-row-[13_/_span_1] xl:tw-col-[2_/_span_2] xl:tw-row-[13_/_span_1]",
  Col1Row3Span1RowSpan1Col: "tw-col-[1_/_span_1] tw-row-[3_/_span_1]",
  Col1Row13Span1RowSpan1Col: "tw-col-[1_/_span_1] tw-row-[13_/_span_1]",
  Col1Row3Span1RowSpan2Cols: "tw-col-[1_/_span_1] tw-row-[3_/_span_1] md:tw-col-[1_/_span_2] md:tw-row-[3_/_span_1]",
  Col3Row7Span2RowsSpan1Col: "tw-col-[1_/_span_1] tw-row-[7_/_span_2] md:tw-col-[2_/_span_1] md:tw-row-[7_/_span_2] xl:tw-col-[3_/_span_1] xl:tw-row-[7_/_span_2]",
  Col1Row13Span1RowSpan2Cols: "tw-col-[1_/_span_1] tw-row-[13_/_span_1] md:tw-col-[1_/_span_2] md:tw-row-[13_/_span_1] xl:tw-col-[1_/_span_2] xl:tw-row-[13_/_span_1]",
  Col1Row11Span3RowsSpan1Col: "tw-col-[1_/_span_1] tw-row-[11_/_span_2] md:tw-col-[1_/_span_1] md:tw-row-[11_/_span_2] xl:tw-col-[1_/_span_1] xl:tw-row-[11_/_span_3]",
} as const;

// Define content position styles
const contentPositionStyles = {
  "top-left": "tw-justify-start tw-items-start",
  "top-center": "tw-justify-center tw-items-start tw-text-center",
  "top-right": "tw-justify-end tw-items-start tw-text-right",
  "center-left": "tw-justify-start tw-items-center",
  "center-center": "tw-justify-center tw-items-center tw-text-center",
  "center-right": "tw-justify-end tw-items-center tw-text-right",
  "bottom-left": "tw-justify-start tw-items-end",
  "bottom-center": "tw-justify-center tw-items-end tw-text-center",
  "bottom-right": "tw-justify-end tw-items-end tw-text-right",
} as const;

export type ContentPosition = keyof typeof contentPositionStyles;

export const PromoBox: React.FC<PromoBoxProps> = ({
  imgSrc,
  imgSrcDesktop,
  imgSrcTablet,
  imgSrcMobile,
  heading,
  paragraph,
  linkHref,
  linkText,
  disableGrid,
  gridPosition,
  contentPosition = "bottom-left",
  className,
}) => {
  if (!imgSrc) {
    return null;
  }

  return (
    <aside
      className={cn(
        "PromoBoxWrapper tw-w-full tw-relative tw-flex tw-overflow-hidden",
        !disableGrid && gridPositions[gridPosition!],

        // Image styling
        "[&_.Image]:tw-absolute [&_.Image]:tw-z-0 [&_.Image]:tw-top-1/2 [&_.Image]:tw-left-1/2 [&_.Image]:tw-transform [&_.Image]:tw--translate-x-1/2 [&_.Image]:tw--translate-y-1/2",
        "[&_.Image_img]:tw-w-full [&_.Image_img]:tw-h-full [&_.Image_img]:tw-object-cover [&_.Image_img]:tw-object-center",

        className
      )}
    >
      <ResponsiveImage alt="" images={{
        default: imgSrc,
        sm: imgSrcMobile,
        md: imgSrcTablet,
        lg: imgSrcTablet,
        xl: imgSrcDesktop,
        "2xl": imgSrcDesktop,
      }} className="Image tw-w-full tw-h-full" />

      <div className={cn(
        "PromoBoxContent tw-absolute tw-z-10 tw-w-full tw-h-full tw-p-5 tw-flex",
        contentPositionStyles[contentPosition]
      )}>
        <div className="tw-max-w-[90%]">
          {heading && (
            <h3 className="tw-text-4xl tw-text-white">{heading}</h3>
          )}

          {paragraph && (
            <p className="tw-mt-3 tw-text-xl tw-text-white">{paragraph}</p>
          )}

          {linkHref && linkText && (
            <a
              className="tw-inline-block tw-mt-3 tw-text-xl tw-text-white tw-underline hover:tw-no-underline hover:!tw-text-white"
              href={linkHref}
            >
              {linkText}
            </a>
          )}
        </div>
      </div>
    </aside>
  );
};
